import { captureException } from '@sentry/nuxt';
import {
  GetNewsArticleDetails,
  GetNewsArticles,
  StrapiArticleType,
  type GetNewsArticleDetailsQuery,
  type GetNewsArticleDetailsQueryVariables,
  type GetNewsArticlesQuery,
  type GetNewsArticlesQueryVariables,
  type StrapiArticle,
} from '#variant/apollo/types/types';
import { useGoogleNews } from '~/helpers/googleNews';
import { PageFetchError } from '~/helpers/fetchErrors';

export default async (articleTypeRef: Ref<StrapiArticleType>) => {
  const { locale } = useI18n();
  const route = useRoute();

  const slug = route.params.slug as string;

  const { data: newsDetail, error } = await useAsyncData(
    `gp-news-detail-${locale.value}-${slug}-${articleTypeRef.value}`,
    async () => {
      const ret: {
        article?: StrapiArticle;
        recentArticles: Array<StrapiArticle>;
        googleNews: string;
      } = {
        recentArticles: [],
        googleNews: null,
      };
      try {
        const { data: response, error: responseError } = await useLegacyApollo<
          GetNewsArticleDetailsQuery,
          GetNewsArticleDetailsQueryVariables
        >(GetNewsArticleDetails, {
          locale: locale.value,
          slug,
          type: articleTypeRef.value,
        });

        if (responseError != null || response == null) {
          throw responseError;
        }

        if (response.articles.length === 0) {
          throw new Error(PageFetchError.NOT_FOUND);
        }

        // We are only interested in the first news (because we always have one
        // news as a result, we match on language and slug).
        const article = response.articles[0] as StrapiArticle;

        // recent articles
        const { data: articlesData } = await useLegacyApollo<
          GetNewsArticlesQuery,
          GetNewsArticlesQueryVariables
        >(GetNewsArticles, {
          locale: locale.value,
          limit: 5,
          // we want to fetch the same article type as the current article
          type: article.type,
        });

        // now we want to make sure that the current article is not in this list of recent articles
        const recentArticlesFiltered = articlesData.articles.filter(
          (a) => a.id !== article.id,
        ) as Array<StrapiArticle>;

        // We are only interested in the first news (because we always have one
        ret.googleNews = JSON.stringify(
          useGoogleNews(route.fullPath, response),
        );
        ret.article = article;
        // if the count of articles is still 5, strip down to four articles
        ret.recentArticles =
          recentArticlesFiltered.length === 5
            ? recentArticlesFiltered.slice(0, 5)
            : recentArticlesFiltered;
      } catch (e: unknown) {
        captureException(e);
        throw e;
      }

      return ret;
    },
  );

  if (newsDetail.value == null || error.value != null) {
    await navigateTo(`/${locale.value}/news`);
  }

  const { redirectToDefaultLocale } = useRedirectToLocaleSlugs(
    newsDetail.value?.article?.localizedSlugs,
  );
  if (newsDetail.value.article.locale !== locale.value) {
    await redirectToDefaultLocale();
  }

  return {
    newsDetail,
  };
};
